/**
 * @fileoverview Loads geographic reference lists for the Location section selects.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getReferenceData } from "../../../logic/companyEdit";
import { logger, sanitizeError } from "../../../utils/logger.js";

/**
 * @param {Function} navigate - Router navigate used by reference-data requests
 * @param {Object} company - Company logged when the fetch starts
 */
export function useLocationReferenceData(navigate, company) {
  const { t } = useTranslation();
  const secondAddressCountryRef = useRef(null);
  const secondAddressStateRef = useRef(null);
  const secondAddressTownRef = useRef(null);
  const secondAddressZipCodeRef = useRef(null);
  const secondAddressNeighborhoodRef = useRef(null);

  const [secondAddressCountries, setSecondAddressCountries] = useState([]);
  const [secondAddressStates, setSecondAddressStates] = useState([]);
  const [generalTowns, setGeneralTowns] = useState([]);
  const [secondAddressTowns, setSecondAddressTowns] = useState([]);
  const [generalZipCodes, setGeneralZipCodes] = useState([]);
  const [secondAddressZipCodes, setSecondAddressZipCodes] = useState([]);
  const [generalNeighborhoods, setGeneralNeighborhoods] = useState([]);
  const [secondAddressNeighborhoods, setSecondAddressNeighborhoods] = useState(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReferenceData = useCallback(async () => {
    try {
      logger.info("company: ", { data: company });
      logger.info("Fetching reference data...");
      setIsLoading(true);
      setError(null);

      const [
        countriesData,
        statesData,
        townsData,
        zipCodesData,
        neighborhoodsData,
      ] = await Promise.all([
        getReferenceData("countries", navigate),
        getReferenceData("states", navigate),
        getReferenceData("towns", navigate),
        getReferenceData("zipCodes", navigate),
        getReferenceData("neighborhoods", navigate),
      ]);

      logger.info("Reference data fetched successfully");
      logger.info("Countries:", { data: countriesData });
      logger.info("States:", { data: statesData });
      logger.info("Towns:", { data: townsData });
      logger.info("Zip Codes:", { data: zipCodesData });
      logger.info("Neighborhoods:", { data: neighborhoodsData });

      setSecondAddressCountries(
        countriesData && countriesData.length > 0 ? countriesData : [],
      );
      setSecondAddressStates(
        statesData && statesData.length > 0 ? statesData : [],
      );
      setGeneralTowns(townsData && townsData.length > 0 ? townsData : []);
      setSecondAddressTowns(townsData && townsData.length > 0 ? townsData : []);
      setGeneralZipCodes(
        zipCodesData && zipCodesData.length > 0 ? zipCodesData : [],
      );
      setSecondAddressZipCodes(
        zipCodesData && zipCodesData.length > 0 ? zipCodesData : [],
      );
      setGeneralNeighborhoods(
        neighborhoodsData && neighborhoodsData.length > 0
          ? neighborhoodsData
          : [],
      );
      setSecondAddressNeighborhoods(
        neighborhoodsData && neighborhoodsData.length > 0
          ? neighborhoodsData
          : [],
      );
    } catch (err) {
      logger.error("Error fetching reference data:", {
        err: sanitizeError(err),
      });
      setError(t("errors.loading.referenceDataFailed"));
      setSecondAddressCountries([]);
      setSecondAddressStates([]);
      setSecondAddressTowns([]);
      setSecondAddressZipCodes([]);
      setSecondAddressNeighborhoods([]);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  return {
    secondAddressCountryRef,
    secondAddressStateRef,
    secondAddressTownRef,
    secondAddressZipCodeRef,
    secondAddressNeighborhoodRef,
    secondAddressCountries,
    secondAddressStates,
    generalTowns,
    secondAddressTowns,
    setSecondAddressTowns,
    generalZipCodes,
    secondAddressZipCodes,
    setSecondAddressZipCodes,
    generalNeighborhoods,
    secondAddressNeighborhoods,
    setSecondAddressNeighborhoods,
    isLoading,
    error,
  };
}
